/**
 * Centralized environment access — fail loud, never fall back to another environment.
 *
 * We deliberately do NOT hardcode URL fallbacks (e.g. `|| 'https://api.taskyinn.com'`): a missing
 * env var must throw, not silently point staging/dev at production. Every service URL comes from the
 * environment (see `config/` central store → rendered `.env`).
 *
 * IMPORTANT: pass the value via a STATIC `process.env.X` reference, e.g.
 *   requireEnv(process.env.NEXT_PUBLIC_NHOST_GRAPHQL_URL, 'NEXT_PUBLIC_NHOST_GRAPHQL_URL')
 * Next.js only inlines `NEXT_PUBLIC_*` vars into the client bundle when they are read as a static
 * member expression — a dynamic `process.env[name]` lookup is `undefined` in the browser. That is why
 * the caller passes the value directly rather than just the name.
 */

/** Assert a required env var is present; throw a clear error if it is missing/empty. */
export function requireEnv(value: string | undefined, name: string): string {
  if (value === undefined || value === '') {
    throw new Error(
      `Missing required environment variable: ${name}. ` +
        `All service URLs must be provided via the environment — there are no hardcoded fallbacks.`,
    )
  }
  return value
}

/** Read an optional env var value, returning `undefined` when unset (use only when truly optional). */
export function optionalEnv(value: string | undefined): string | undefined {
  return value === undefined || value === '' ? undefined : value
}

/**
 * Hasura URL for SERVER-SIDE calls. Prefers the internal compose-DNS URL
 * (`NHOST_GRAPHQL_INTERNAL_URL`, same box, ~2 ms) over the public one, which
 * hairpins through Cloudflare at ~200 ms per hop. Measured 2026-09-06: a 3-room
 * group booking made ~39 sequential hops and took 8.4 s on the public URL.
 * Safe in a client bundle too — the internal var is unset there and the
 * `NEXT_PUBLIC_*` read stays a static member expression, so it inlines.
 */
export function serverGraphqlUrl(): string {
  return requireEnv(
    process.env.NHOST_GRAPHQL_INTERNAL_URL ||
      process.env.NHOST_GRAPHQL_URL ||
      process.env.HASURA_GRAPHQL_ENDPOINT ||
      process.env.NEXT_PUBLIC_NHOST_GRAPHQL_URL,
    'NHOST_GRAPHQL_INTERNAL_URL / NHOST_GRAPHQL_URL / NEXT_PUBLIC_NHOST_GRAPHQL_URL',
  )
}

/** Nhost auth URL for SERVER-SIDE calls — same internal-first rule as `serverGraphqlUrl`. */
export function serverAuthUrl(): string {
  return requireEnv(
    process.env.NHOST_AUTH_INTERNAL_URL ||
      process.env.NHOST_AUTH_URL ||
      process.env.NEXT_PUBLIC_NHOST_AUTH_URL,
    'NHOST_AUTH_INTERNAL_URL / NHOST_AUTH_URL / NEXT_PUBLIC_NHOST_AUTH_URL',
  )
}
